import { useCallback, useEffect, useState } from 'react';
import { getTemplateHelpers } from '../api/templateApi';
import { TemplateHelperMetadata, TemplateHelperCategory, TemplateHelperType } from '../types';
import {
  TEMPLATE_HELPERS_CACHE_VERSION,
  TEMPLATE_HELPERS_DEFAULT_TTL_MS,
  clearTemplateHelpersSessionStorage,
  getDefaultTemplateHelpersCacheScope,
  getTemplateHelpersFromSessionStorage,
  saveTemplateHelpersToSessionStorage,
} from '../services/templateHelpersSessionStorage';

const VALID_CATEGORIES: TemplateHelperCategory[] = ['conditional', 'standard', 'string'];
const VALID_TYPES: TemplateHelperType[] = ['helper', 'placeholder'];

interface UseTemplateHelpersOptions {
  enabled?: boolean;
  cacheScope?: string;
  cacheVersion?: string;
  ttlMs?: number;
  useStaleOnError?: boolean;
}

interface UseTemplateHelpersReturn {
  helpers: TemplateHelperMetadata[];
  loading: boolean;
  error: boolean;
  refetch: () => Promise<void>;
}

const normalizeHelperMetadata = (item: unknown): TemplateHelperMetadata | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const input = item as Record<string, unknown>;
  const className = String(input.class ?? '').trim();
  const category = String(input.category ?? '').trim().toLowerCase();
  const startKey = String(input.startKey ?? '').trim();
  const endKeyRaw = input.endKey;
  const type = String(input.type ?? '').trim().toLowerCase();
  const endKey = endKeyRaw == null ? null : String(endKeyRaw).trim();

  if (!className || !startKey || !VALID_CATEGORIES.includes(category as TemplateHelperCategory)) {
    return null;
  }
  if (!VALID_TYPES.includes(type as TemplateHelperType)) {
    return null;
  }

  return {
    class: className,
    category: category as TemplateHelperCategory,
    startKey,
    endKey,
    type: type as TemplateHelperType,
  };
};

const normalizeHelpersList = (items: unknown): TemplateHelperMetadata[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  const dedup = new Map<string, TemplateHelperMetadata>();
  items.forEach((item) => {
    const normalized = normalizeHelperMetadata(item);
    if (!normalized) {
      return;
    }
    const key = `${normalized.type}|${normalized.category}|${normalized.startKey}|${normalized.endKey ?? ''}`;
    dedup.set(key, normalized);
  });
  return Array.from(dedup.values());
};

export const useTemplateHelpers = (options: UseTemplateHelpersOptions = {}): UseTemplateHelpersReturn => {
  const {
    enabled = true,
    cacheScope = getDefaultTemplateHelpersCacheScope(),
    cacheVersion = TEMPLATE_HELPERS_CACHE_VERSION,
    ttlMs = TEMPLATE_HELPERS_DEFAULT_TTL_MS,
    useStaleOnError = true,
  } = options;

  const [helpers, setHelpers] = useState<TemplateHelperMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<boolean>(false);

  const fetchHelpers = useCallback(async (skipCache = false) => {
    if (!enabled) {
      setLoading(false);
      setError(false);
      setHelpers([]);
      return;
    }

    setLoading(true);
    setError(false);

    if (!skipCache) {
      const cachedHelpers = getTemplateHelpersFromSessionStorage({
        scopeKey: cacheScope,
        version: cacheVersion,
      });

      if (cachedHelpers && cachedHelpers.length > 0) {
        setHelpers(cachedHelpers);
        setLoading(false);
        return;
      }
    }

    try {
      const apiResponse = await getTemplateHelpers();
      const normalized = normalizeHelpersList(apiResponse);
      saveTemplateHelpersToSessionStorage(normalized, cacheScope, cacheVersion, ttlMs);
      setHelpers(normalized);
    } catch (fetchError) {
      const staleCache = getTemplateHelpersFromSessionStorage({
        scopeKey: cacheScope,
        version: cacheVersion,
        allowStale: useStaleOnError,
      });

      if (staleCache && staleCache.length > 0) {
        setHelpers(staleCache);
      } else {
        setHelpers([]);
        setError(true);
      }

      // Keep warning only for diagnostics, no noisy UI for graceful fallback.
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch template helpers metadata:', fetchError);
    } finally {
      setLoading(false);
    }
  }, [cacheScope, cacheVersion, enabled, ttlMs, useStaleOnError]);

  useEffect(() => {
    fetchHelpers();
  }, [fetchHelpers]);

  const refetch = useCallback(async () => {
    clearTemplateHelpersSessionStorage(cacheScope);
    await fetchHelpers(true);
  }, [cacheScope, fetchHelpers]);

  return { helpers, loading, error, refetch };
};
